// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useRef, useState } from "react";
import { SigningRequestStatus } from "@/lib/signing/types";
/** The waits between reloads: about a second, then two. */
var SETTLE_POLL_DELAYS_MS = [1000, 1000, 1500, 2000];
/** How long a panel waits for the action to run before it stops asking. */
export var SETTLE_POLL_MAX_MS = 3 * 60000;
/**
 * The wait before reload number `attempt` (from 0), or `null` once the waits
 * would pass {@link SETTLE_POLL_MAX_MS}.
 */
export function settleDelay(attempt) {
    var delayOf = function (n) {
        return SETTLE_POLL_DELAYS_MS[Math.min(n, SETTLE_POLL_DELAYS_MS.length - 1)];
    };
    var total = 0;
    for (var n = 0; n <= attempt; n++) {
        total += delayOf(n);
    }
    return total > SETTLE_POLL_MAX_MS ? null : delayOf(attempt);
}
/**
 * While a request has all its signatures but its action hasn't run yet
 * (Completed: the action was dispatched), reloads it with a backoff until it
 * is Executed or Failed, for a few minutes at most. Only while `active`.
 */
export function useSettlePolling(status, active, reload) {
    var attempt = useRef(0);
    var reloadRef = useRef(reload);
    reloadRef.current = reload;
    // Counts reloads, so a reload that changed nothing (or failed) schedules the next.
    var _a = useState(0), tick = _a[0], setTick = _a[1];
    var settling = active && status === SigningRequestStatus.Completed;
    useEffect(function () {
        if (!settling) {
            attempt.current = 0;
            return;
        }
        var delay = settleDelay(attempt.current);
        if (delay === null)
            return;
        var cancelled = false;
        var timer = setTimeout(function () {
            attempt.current += 1;
            reloadRef
                .current()
                .catch(function () { return undefined; })
                .finally(function () {
                if (!cancelled)
                    setTick(function (n) { return n + 1; });
            });
        }, delay);
        return function () {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [settling, tick]);
}
